// A minimal Checkbox primitive — pure Tailwind, no Radix dep.
// Matches the shadcn Checkbox API closely so we can swap in
// @radix-ui/react-checkbox later without refactoring callers. Like the shadcn
// original it renders a <button role="checkbox">, so a data-testid placed on it
// lands on the element QA actually clicks.

import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface CheckboxProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "type"> {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ className, checked, onCheckedChange, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={checked}
      data-state={checked ? "checked" : "unchecked"}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onCheckedChange?.(!checked);
      }}
      className={cn(
        "peer inline-flex size-4 shrink-0 items-center justify-center rounded-sm border-2 transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        // A checkbox is a control, not data, so its "on" state is the monochrome
        // primary — the DS keeps blue off control fills.
        checked ? "border-primary bg-primary text-primary-foreground" : "border-border-strong bg-raised",
        className,
      )}
      {...props}
    >
      {checked && <Check className="size-3" strokeWidth={3} />}
    </button>
  ),
);
Checkbox.displayName = "Checkbox";
